import React, { useState } from "react";
import { usePayrollHistory } from "./usePayrollHistory";
import { monthNames, formatDot } from "../../../util/rekapAbsensiUtils";
import { exportPayrollExcel } from "../../../util/payrollExcel";
import DetailAbsensiTable from "../../../component/DetailAbsensiTable/DetailAbsensiTable";
import RincianPerhitunganPayroll from "../../../component/RincianPerhitunganPayroll/RincianPerhitunganPayroll";
import PopUpComponent from "../../../component/PopUpComponent/PopUpComponent";
import CloseIcon from "../../../asset/close_icon.png";
import { hasRole } from "../../../util/function";
import "./PayrollHistory.css";

const formatTanggal = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(`${dateStr}T00:00:00`);
  if (isNaN(d)) return dateStr;
  return `${String(d.getDate()).padStart(2, "0")} ${monthNames[
    d.getMonth()
  ].slice(0, 3)} ${d.getFullYear()}`;
};

const formatWaktu = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (isNaN(d)) return "-";
  return `${String(d.getDate()).padStart(2, "0")} ${monthNames[
    d.getMonth()
  ].slice(0, 3)} ${d.getFullYear()}, ${String(d.getHours()).padStart(
    2,
    "0"
  )}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const labelTipe = (type) => (type === "permanent" ? "Permanen" : "Kontrak");

const blankIfZero = (v) => {
  const n = Number(v);
  return !v || Number.isNaN(n) || n === 0 ? "" : v;
};

const PayrollHistoryEmployeeRow = ({
  row,
  index,
  typeKaryawan,
  periodeStart,
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr
        className={`ph-emp-row${expanded ? " expanded" : ""}`}
        onClick={() => setExpanded(!expanded)}
      >
        <td className="ph-detail-td">{index + 1}</td>
        <td className="ph-detail-td">
          <span className={`ph-emp-arrow${expanded ? " expanded" : ""}`}>
            ▶
          </span>
          {row.name}
        </td>
        <td className="ph-detail-td">{row.bagian}</td>
        <td className="ph-detail-td">{blankIfZero(row.jamHalf)}</td>
        <td className="ph-detail-td">{blankIfZero(row.totalHariKerja)}</td>
        <td className="ph-detail-td ph-cell-probation">
          {blankIfZero(row.jamHalfProbation ?? row.hariHalfProbation)}
        </td>
        <td className="ph-detail-td ph-cell-probation">
          {blankIfZero(row.totalHariKerjaProbation)}
        </td>
      </tr>

      {expanded && (
        <tr>
          <td colSpan={7} className="ph-emp-expand-td">
            <div className="ph-emp-panel">
              <div className="ph-emp-panel-header">
                Rincian Perhitungan — {row.userId} {row.name}
              </div>
              <div className="ph-emp-panel-body">
                <div className="ph-emp-col-rincian">
                  <RincianPerhitunganPayroll
                    row={row}
                    typeKaryawan={typeKaryawan}
                    periodeStart={periodeStart}
                    bagian={row.bagian}
                    keteranganKurangBayar={row.keteranganKurangBayar}
                    keteranganLebihBayar={row.keteranganLebihBayar}
                  />
                </div>
                <div className="ph-emp-col-detail">
                  <DetailAbsensiTable detail={row.detail} userId={row.userId} />
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const DetailKaryawanTable = ({ rows, typeKaryawan, periodeStart }) => {
  const [search, setSearch] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const handleSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
  };

  const sortIndicator = (key) =>
    sortConfig.key === key
      ? sortConfig.direction === "asc"
        ? " ▲"
        : " ▼"
      : "";

  const getSortValue = (r, key) => {
    if (key === "name" || key === "bagian") return String(r[key] || "");
    if (key === "jamHalfProbation")
      return Number(r.jamHalfProbation ?? r.hariHalfProbation) || 0;
    return Number(r[key]) || 0;
  };

  const list = Array.isArray(rows) ? rows : [];

  const keyword = search.trim().toLowerCase();
  const filtered = keyword
    ? list.filter(
        (r) =>
          (r.name || "").toLowerCase().includes(keyword) ||
          (r.bagian || "").toLowerCase().includes(keyword)
      )
    : list;

  const sorted = sortConfig.key
    ? [...filtered].sort((a, b) => {
        const va = getSortValue(a, sortConfig.key);
        const vb = getSortValue(b, sortConfig.key);
        const result =
          typeof va === "string"
            ? va.localeCompare(vb, "id", { sensitivity: "base" })
            : va - vb;
        return sortConfig.direction === "asc" ? result : -result;
      })
    : filtered;

  return (
    <div className="ph-detail-wrapper">
      <div className="ph-detail-header">
        Rincian Karyawan ({filtered.length}
        {keyword ? ` dari ${list.length}` : ""} Orang) — klik nama untuk lihat
        rincian perhitungan &amp; detail absensi
      </div>

      <div className="ph-detail-searchbox">
        <input
          type="text"
          className="ph-detail-search"
          placeholder="Cari nama atau bagian karyawan"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="ph-detail-scroll">
        <table className="ph-detail-table">
          <thead>
            <tr className="ph-detail-thead-row">
              <th className="ph-detail-th">No.</th>
              <th
                className="ph-detail-th ph-th-sortable"
                onClick={() => handleSort("name")}
              >
                Nama{sortIndicator("name")}
              </th>
              <th
                className="ph-detail-th ph-th-sortable"
                onClick={() => handleSort("bagian")}
              >
                Bagian{sortIndicator("bagian")}
              </th>
              <th
                className="ph-detail-th ph-th-sortable"
                onClick={() => handleSort("jamHalf")}
              >
                &#189; Hari (Jam){sortIndicator("jamHalf")}
              </th>
              <th
                className="ph-detail-th ph-th-sortable"
                onClick={() => handleSort("totalHariKerja")}
              >
                Total Hari{sortIndicator("totalHariKerja")}
              </th>
              <th
                className="ph-detail-th ph-th-sortable ph-th-probation"
                onClick={() => handleSort("jamHalfProbation")}
              >
                &#189; Hari Probation (Jam){sortIndicator("jamHalfProbation")}
              </th>
              <th
                className="ph-detail-th ph-th-sortable ph-th-probation"
                onClick={() => handleSort("totalHariKerjaProbation")}
              >
                Hari Probation{sortIndicator("totalHariKerjaProbation")}
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="ph-detail-empty">
                  {keyword
                    ? "Karyawan tidak ditemukan"
                    : "Tidak ada rincian karyawan"}
                </td>
              </tr>
            ) : (
              sorted.map((row, i) => (
                <PayrollHistoryEmployeeRow
                  key={row.userId || i}
                  row={row}
                  index={i}
                  typeKaryawan={typeKaryawan}
                  periodeStart={periodeStart}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const PayrollHistoryRow = ({
  batch,
  index,
  detail,
  detailLoading,
  onToggle,
  onBatalkan,
  jsonUser,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [showInfo, setShowInfo] = useState(false);

  const toggle = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next) await onToggle(batch._id);
  };

  const rows = detail?.rows || [];

  const handleExport = (e) => {
    e.stopPropagation();
    if (rows.length === 0) return;
    exportPayrollExcel(
      rows,
      `Payroll ${labelTipe(batch.typeKaryawan)} ${batch.resetDate}.xlsx`,
      batch.periodeEnd
    );
  };

  return (
    <>
      <tr className={`ph-row${expanded ? " expanded" : ""}`} onClick={toggle}>
        <td>{index + 1}</td>
        <td>
          <span className={`ph-arrow${expanded ? " expanded" : ""}`}>▶</span>
          {formatTanggal(batch.resetDate)}
        </td>
        <td className="ph-periode-cell">
          {formatTanggal(batch.periodeStart)} –{" "}
          {formatTanggal(batch.periodeEnd)}
        </td>
        <td>
          <span className={`ph-badge ph-badge-${batch.typeKaryawan}`}>
            {labelTipe(batch.typeKaryawan)}
          </span>
        </td>
        <td>{batch.totalKaryawan}</td>
        {hasRole(jsonUser, "super_user", "finance_admin") ? (
          <td className="ph-right ph-strong">
            Rp {formatDot(batch.totalTakeHomePay)}
          </td>
        ) : (
          ""
        )}
        <td>
          <div className="ph-row-actions">
            <button
              className="ph-btn ph-btn-export"
              onClick={handleExport}
              disabled={!expanded || rows.length === 0}
              title={
                expanded ? "Unduh Excel" : "Buka rincian dulu untuk mengunduh"
              }
            >
              Excel
            </button>
            {hasRole(jsonUser, "super_user") ? (
              <button
                className="ph-btn ph-btn-batal"
                onClick={(e) => {
                  e.stopPropagation();
                  onBatalkan(batch);
                }}
              >
                Batalkan
              </button>
            ) : (
              ""
            )}
          </div>
        </td>
      </tr>

      {expanded && (
        <tr>
          <td colSpan="7" className="ph-expand-td">
            <div className="ph-panel">
              <div className="ph-panel-header">
                Payroll {labelTipe(batch.typeKaryawan)} — di-reset{" "}
                {formatTanggal(batch.resetDate)} (periode absensi{" "}
                {formatTanggal(batch.periodeStart)} s/d{" "}
                {formatTanggal(batch.periodeEnd)})
              </div>

              <div
                className="ph-panel-toolbar"
                style={{ justifyContent: "flex-start" }}
              >
                <button
                  className="ph-btn ph-btn-netral"
                  onClick={() => setShowInfo(!showInfo)}
                >
                  {showInfo ? "Sembunyikan Keterangan" : "Tampilkan Keterangan"}
                </button>
              </div>

              <div
                className={`ph-panel-body${
                  showInfo ? "" : " ph-panel-body-full"
                }`}
              >
                {showInfo && (
                  <div className="ph-panel-col-kiri">
                    <div className="ph-stat">
                      <span>Total dibayarkan</span>
                      <strong className="ph-stat-hijau">
                        Rp {formatDot(batch.totalTakeHomePay)}
                      </strong>
                    </div>
                    <div className="ph-stat">
                      <span>Jumlah karyawan</span>
                      <strong>{batch.totalKaryawan} orang</strong>
                    </div>
                    <div className="ph-stat">
                      <span>
                        BPJS Ketenagakerjaan ({batch.countBpjsTK || 0} orang)
                      </span>
                      <strong>Rp {formatDot(batch.totalBpjsTK)}</strong>
                    </div>
                    <div className="ph-stat">
                      <span>
                        BPJS Kesehatan ({batch.countBpjsK || 0} orang)
                      </span>
                      <strong>Rp {formatDot(batch.totalBpjsK)}</strong>
                    </div>
                    <div className="ph-stat">
                      <span>Periode absensi</span>
                      <strong>
                        {formatTanggal(batch.periodeStart)} –{" "}
                        {formatTanggal(batch.periodeEnd)}
                      </strong>
                    </div>
                    <div className="ph-stat">
                      <span>Diproses pada</span>
                      <strong>{formatWaktu(batch.createdAt)}</strong>
                    </div>
                    <div className="ph-stat">
                      <span>Diproses Oleh</span>
                      <strong>{batch.resetBy}</strong>
                    </div>

                    {batch.catatan ? (
                      <div className="ph-stat ph-stat-catatan">
                        <span>Catatan</span>
                        <strong>{batch.catatan}</strong>
                      </div>
                    ) : null}
                  </div>
                )}

                <div className="ph-panel-col-kanan">
                  {detailLoading === batch._id ? (
                    <div className="ph-detail-loading">Memuat rincian…</div>
                  ) : (
                    <DetailKaryawanTable
                      rows={rows}
                      typeKaryawan={batch.typeKaryawan}
                      periodeStart={batch.periodeStart}
                    />
                  )}
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const PayrollHistory = () => {
  const {
    jsonUser,
    historyList,
    detailCache,
    detailLoading,
    getDetail,
    loading,
    errorMessage,
    resetDateFrom,
    setResetDateFrom,
    resetDateTo,
    setResetDateTo,
    typeKaryawan,
    setTypeKaryawan,
    setValueSearch,
    batalkanTarget,
    setBatalkanTarget,
    batalkanLoading,
    handleBatalkanPayroll,
  } = usePayrollHistory();

  const totalSemua = historyList.reduce(
    (sum, b) => sum + (b.totalTakeHomePay || 0),
    0
  );

  return (
    <div className="container-payroll-history">
      {batalkanTarget ? (
        <PopUpComponent>
          <div className="ph-popup-header-row">
            <p className="ph-popup-title">Batalkan payroll ini?</p>
            <img
              src={CloseIcon}
              onClick={() => setBatalkanTarget(null)}
              className="ph-popup-close-icon"
              alt="Tutup"
            />
          </div>
          <p className="ph-popup-desc">
            Absensi, lembur, dan request pada periode{" "}
            {formatTanggal(batalkanTarget.periodeStart)} –{" "}
            {formatTanggal(batalkanTarget.periodeEnd)} (di-reset{" "}
            {formatTanggal(batalkanTarget.resetDate)}) akan terbuka kembali dan
            muncul lagi di Rekap Absensi. Riwayat ini akan dihapus.
          </p>
          <div className="ph-popup-actions">
            <button
              className="ph-btn ph-btn-netral"
              onClick={() => setBatalkanTarget(null)}
            >
              Kembali
            </button>
            <button
              className={`ph-btn ph-btn-batal${
                batalkanLoading ? " is-loading" : ""
              }`}
              disabled={batalkanLoading}
              onClick={handleBatalkanPayroll}
            >
              {batalkanLoading ? "Memproses..." : "Batalkan payroll"}
            </button>
          </div>
        </PopUpComponent>
      ) : null}

      <div className="wrapper-card-payroll-history">
        <div className="ph-header-row">
          <div className="ph-filter-group">
            <div className="ph-filter-item">
              <p>Tanggal Reset Dari</p>
              <input
                type="date"
                value={resetDateFrom}
                onChange={(e) => setResetDateFrom(e.target.value)}
              />
            </div>
            <div className="ph-filter-item">
              <p>Tanggal Reset Sampai</p>
              <input
                type="date"
                value={resetDateTo}
                onChange={(e) => setResetDateTo(e.target.value)}
              />
            </div>
            <div className="ph-filter-item">
              <p>Karyawan</p>
              <select
                style={{ width: "100%" }}
                value={typeKaryawan}
                onChange={(e) => setTypeKaryawan(e.target.value)}
              >
                <option value="">Semua</option>
                <option value="contract">Kontrak</option>
                <option value="permanent">Permanen</option>
              </select>
            </div>
          </div>
          {hasRole(jsonUser, "super_user", "finance_admin") ? (
            <div className="ph-summary">
              <span>Total pada filter ini</span>
              <strong>Rp {formatDot(totalSemua)}</strong>
            </div>
          ) : (
            ""
          )}
        </div>

        <input
          type="text"
          className="ph-search-input"
          placeholder="Cari periode, catatan, atau nama pemroses"
          onChange={(e) => setValueSearch(e.target.value)}
        />

        {errorMessage ? <p className="ph-error">{errorMessage}</p> : null}

        <div className="table-wrapper-payroll-history">
          <table className="table_dashboard mobile-font-size-10">
            <thead>
              <tr>
                <th>No.</th>
                <th>Tanggal Reset</th>
                <th>Periode Absensi</th>
                <th>Tipe</th>
                <th>Karyawan</th>
                {hasRole(jsonUser, "super_user", "finance_admin") ? (
                  <th className="ph-right">Total Dibayarkan</th>
                ) : (
                  ""
                )}
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="ph-center">
                    Loading...
                  </td>
                </tr>
              ) : historyList.length === 0 ? (
                <tr>
                  <td colSpan="7" className="ph-center">
                    Belum ada payroll yang diproses. Jalankan Reset di halaman
                    Rekap Absensi untuk membuat riwayat pertama.
                  </td>
                </tr>
              ) : (
                historyList.map((batch, index) => (
                  <PayrollHistoryRow
                    key={batch._id}
                    batch={batch}
                    index={index}
                    detail={detailCache[batch._id]}
                    detailLoading={detailLoading}
                    onToggle={getDetail}
                    onBatalkan={setBatalkanTarget}
                    jsonUser={jsonUser}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PayrollHistory;
